import { Button } from 'antd';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import Link from 'next/link';

import { formatNumberToK } from '@/shared/utils';
import { ThreadsUserModelV1 } from '@/types/threads';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';

interface ThreadsUserInfoProps {
  user: ThreadsUserModelV1;
}

const ThreadsUserInfo: React.FC<ThreadsUserInfoProps> = ({ user }) => {
  const { t } = useTranslation();

  if (!user) return null;

  return (
    <div className="flex justify-between items-center py-4 max-md:flex-col max-md:gap-8" id="threads-user-info">
      {/* Threads 用户信息 */}
      <div className="flex items-center max-md:items-start">
        <div className="relative h-12 w-12 mr-4 flex-shrink-0">
          <Image
            src={user.avatar || '/images/layouts/placeholder-icon.webp'}
            alt={user.username || 'user avatar'}
            width={48}
            height={48}
            className="rounded-full object-cover"
            loading="lazy"
          />
        </div>

        <div className="flex flex-col">
          <div className="flex items-center">
            <span className="font-semibold text-[24px] leading-7 max-md:text-[16px] max-md:leading-5">
              {user.full_name || user.username}
            </span>
            <span className="ml-2 mr-1 text-sm text-[#0C0C0C99] max-md:text-[13px]">{user.username}</span>
            {user.is_verified && (
              <Image
                src="/images/follow/blue-mark.webp"
                alt="verified"
                width={16}
                height={16}
                className="rounded-full"
              />
            )}
          </div>

          {user.profile && <div className="mt-1.5 text-[#0C0C0C99] max-md:text-[13px] leading-4">{user.profile}</div>}

          {user.follower_count !== undefined && (
            <div className="mt-2 max-md:text-[13px] leading-4">
              <span>{formatNumberToK(user.follower_count)}</span>
              <span className="ml-1 text-[#0C0C0C99]">{t(user.follower_count === 1 ? 'follower' : 'followers')}</span>
            </div>
          )}
        </div>
      </div>

      {/* View Instagram Posts 按钮 */}
      <Link
        href={`/web-viewer-for-instagram?media_name=${user.username}`}
        target="_blank"
        className="w-[248px] h-[54px] max-md:w-full"
        onClick={() => {
          ga_logEvent(EventName.threads_downloader_s_r_view_btn_clicked);
        }}
      >
        <Button
          className="w-full h-full rounded-md"
          icon={<Image src="/images/follow/ins-mark.webp" alt="ins-mark" width={22} height={22} />}
          style={{
            backgroundImage: 'url(/images/threads-downloader/td-view-btn-bg.webp)',
            backgroundSize: 'auto',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          {t('View Instagram Posts')}
          <Image src="/images/threads-downloader/td-right-arrow-icon.webp" alt="right-arrow" width={20} height={20} />
        </Button>
      </Link>
    </div>
  );
};

export default ThreadsUserInfo;
